import { useCallback, useState } from "react"

import type { ToggleableColumn } from "../components/test-case-list/TestCaseColumnVisibility"
import { EVENTS, useEventDispatcher } from "../event-bus"

const DEFAULT_VISIBLE_COLUMNS: ToggleableColumn[] = ["type", "metadata"]

/**
 * Manages table-level UI state: which columns are visible and whether all
 * rows are expanded.  Column visibility can be seeded after initialization
 * (e.g. auto-show KB entries) via the exposed `setVisibleColumns` setter.
 */
export function useTableState() {
  const [visibleColumns, setVisibleColumns] = useState<Set<ToggleableColumn>>(
    () => new Set<ToggleableColumn>(DEFAULT_VISIBLE_COLUMNS),
  )

  const toggleColumn = useCallback((key: ToggleableColumn) => {
    setVisibleColumns((prev) => {
      const next = new Set(prev)
      if (next.has(key)) {
        next.delete(key)
      } else {
        next.add(key)
      }
      return next
    })
  }, [])

  const [isAllExpanded, setIsAllExpanded] = useState(false)
  const dispatch = useEventDispatcher()

  const handleToggleExpandAll = useCallback(() => {
    if (isAllExpanded) {
      dispatch(EVENTS.COLLAPSE_ALL, {})
    } else {
      dispatch(EVENTS.EXPAND_ALL, {})
    }
    setIsAllExpanded((prev) => !prev)
  }, [isAllExpanded, dispatch])

  return {
    visibleColumns,
    setVisibleColumns,
    toggleColumn,
    isAllExpanded,
    handleToggleExpandAll,
  }
}
